type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface AvatarProps {
  firstName?: string | null
  lastName?: string | null
  size?: AvatarSize
  className?: string
}

const sizeClasses: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-xs',
  sm: 'h-8 w-8 text-sm',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
}

// Generate a consistent color based on the name
function getColorFromName(name: string): string {
  const colors = [
    'bg-red-500',
    'bg-orange-500',
    'bg-amber-500',
    'bg-yellow-500',
    'bg-lime-500',
    'bg-green-500',
    'bg-emerald-500',
    'bg-teal-500',
    'bg-cyan-500',
    'bg-sky-500',
    'bg-blue-500',
    'bg-indigo-500',
    'bg-violet-500',
    'bg-purple-500',
    'bg-fuchsia-500',
    'bg-pink-500',
    'bg-rose-500',
  ]

  // Simple hash function to get consistent color for same name
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }

  return colors[Math.abs(hash) % colors.length]
}

function getInitials(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.trim().charAt(0).toUpperCase() || ''
  const last = lastName?.trim().charAt(0).toUpperCase() || ''
  return first + last || '?'
}

export function Avatar({ firstName, lastName, size = 'md', className = '' }: AvatarProps) {
  const initials = getInitials(firstName, lastName)
  const fullName = `${firstName || ''} ${lastName || ''}`.trim() || 'Unknown'
  const bgColor = getColorFromName(fullName)

  return (
    <span
      className={`inline-flex items-center justify-center rounded-full text-white font-medium ${sizeClasses[size]} ${bgColor} ${className}`}
      title={fullName}
    >
      {initials}
    </span>
  )
}

// Convenient wrapper for displaying avatar with name
interface AvatarWithNameProps extends AvatarProps {
  showName?: boolean
  nameClassName?: string
}

export function AvatarWithName({
  firstName,
  lastName,
  size = 'sm',
  showName = true,
  className = '',
  nameClassName = ''
}: AvatarWithNameProps) {
  const fullName = `${firstName || ''} ${lastName || ''}`.trim() || 'Unknown'

  return (
    <div className={`flex items-center gap-x-3 ${className}`}>
      <Avatar firstName={firstName} lastName={lastName} size={size} />
      {showName && (
        <span className={nameClassName}>
          {fullName}
        </span>
      )}
    </div>
  )
}
